import { useEffect, useState } from 'react';
import { api, uid, ApiError } from '../lib/api';
import { useToast } from '../context/ToastContext';

interface WashBatch {
  id: string; lot_number: string; day: string; quantities: Record<string, number>;
  reception_ref?: string | null; operator: string; photo_id?: string | null;
  actor: string; created_at: string;
}

interface ReceptionMovement {
  ref?: string | null; day: string; item: string; qty: number; meta?: { truck?: string };
}

interface ReceptionOption { ref: string; day: string; truck: string; items: Record<string, number>; }

export function Hygiene() {
  const toast = useToast();
  const [pm, setPm] = useState('');
  const [gm, setGm] = useState('');
  const [day, setDay] = useState(() => new Date().toISOString().slice(0, 10));
  const [receptionRef, setReceptionRef] = useState('');
  const [receptionOptions, setReceptionOptions] = useState<ReceptionOption[]>([]);
  const [operator, setOperator] = useState('');
  const [photoId, setPhotoId] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; err: boolean } | null>(null);
  const [batches, setBatches] = useState<WashBatch[]>([]);

  function loadBatches() {
    api<WashBatch[]>('/v1/wash-batches').then(setBatches).catch(() => {});
  }

  function loadReceptions() {
    api<ReceptionMovement[]>('/v1/movements?kind=reception&limit=200').then((ms) => {
      const byRef = new Map<string, ReceptionOption>();
      for (const m of ms) {
        if (!m.ref) continue;
        const o = byRef.get(m.ref) || { ref: m.ref, day: m.day, truck: m.meta?.truck || '', items: {} };
        o.items[m.item] = (o.items[m.item] || 0) + m.qty;
        byRef.set(m.ref, o);
      }
      setReceptionOptions(Array.from(byRef.values()).sort((a, b) => b.day.localeCompare(a.day)));
    }).catch(() => {});
  }

  useEffect(() => { loadBatches(); loadReceptions(); }, []);

  async function onPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const id = uid();
    setPreview(URL.createObjectURL(file));
    setBusy(true);
    try {
      await api(`/v1/photos?id=${encodeURIComponent(id)}&kind=wash`, {
        method: 'POST', raw: true, headers: { 'Content-Type': file.type || 'image/jpeg' }, body: file,
      });
      setPhotoId(id);
    } catch (e) { setMsg({ text: e instanceof ApiError ? e.message : 'Envoi de la photo impossible', err: true }); }
    finally { setBusy(false); }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const quantities: Record<string, number> = {};
    if (+pm > 0) quantities.PM = +pm;
    if (+gm > 0) quantities.GM = +gm;
    if (!Object.keys(quantities).length) { setMsg({ text: 'Indiquez au moins une quantité.', err: true }); return; }
    if (!operator.trim()) { setMsg({ text: 'Indiquez le responsable du lavage.', err: true }); return; }
    if (!receptionRef) { setMsg({ text: 'Choisissez la réception liée à ce lot.', err: true }); return; }
    const body: Record<string, unknown> = {
      quantities, day, operator: operator.trim(),
      reception_ref: receptionRef, photo_id: photoId || undefined, ref: uid(),
    };
    try {
      const r = await api<{ replayed: boolean; lot_number: string; queued?: true }>('/v1/wash-batches', {
        method: 'POST', body, offlineQueue: true, queueKind: 'wash_batch', queueLabel: `Lot lavage — réception ${receptionRef}`,
      });
      if (r.queued) {
        setMsg({ text: 'Hors ligne — lot enregistré, le numéro de lot sera attribué à la synchronisation.', err: false });
        toast('Lot de lavage mis en attente (hors ligne)');
      } else {
        setMsg({ text: `Lot ${r.lot_number} enregistré.`, err: false });
        toast(`Lot de lavage ${r.lot_number} enregistré`);
        loadBatches();
      }
      setPm(''); setGm(''); setReceptionRef(''); setOperator(''); setPhotoId(null); setPreview(null);
    } catch (e) { setMsg({ text: e instanceof ApiError ? e.message : 'Erreur', err: true }); }
  }

  return (
    <>
      <div className="pagehead"><h1>Lavage des caisses (traçabilité ONSSA)</h1></div>
      <div className="card">
        <h2>Déclarer un lot lavé</h2>
        <p className="muted">Chaque lot reçoit un numéro unique, lié à une réception, conservé pour la traçabilité sanitaire.</p>
        <form onSubmit={submit}>
          <div className="row2">
            <div><label>Quantité PM</label><input type="number" min={0} value={pm} onChange={(e) => setPm(e.target.value)} /></div>
            <div><label>Quantité GM</label><input type="number" min={0} value={gm} onChange={(e) => setGm(e.target.value)} /></div>
          </div>
          <label>Date</label>
          <input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          <label>Responsable du lavage</label>
          <input value={operator} onChange={(e) => setOperator(e.target.value)} placeholder="nom de l'opérateur" />
          <label>Réception liée</label>
          <select value={receptionRef} onChange={(e) => setReceptionRef(e.target.value)} required>
            <option value="">—</option>
            {receptionOptions.map((o) => (
              <option key={o.ref} value={o.ref}>
                {o.ref} — {o.day}{o.truck ? ` — ${o.truck}` : ''} — {Object.entries(o.items).map(([k, v]) => `${k}:${v}`).join(' ')}
              </option>
            ))}
          </select>
          {!receptionOptions.length && <p className="muted" style={{ marginTop: 4 }}>Aucune réception avec référence trouvée. Une réception doit être enregistrée avec une référence avant de pouvoir déclarer son lavage.</p>}
          <label>Photo du lot (optionnel)</label>
          <input type="file" accept="image/*" capture="environment" onChange={onPhoto} disabled={busy} />
          {preview && <img src={preview} className="photo-preview" style={{ maxWidth: 160, marginTop: 6 }} />}
          <div className="row" style={{ marginTop: '0.8rem' }}>
            <button type="submit" className="primary" disabled={busy}>Enregistrer le lot</button>
          </div>
          {msg && <div className={msg.err ? 'err' : 'ok'}>{msg.text}</div>}
        </form>
      </div>
      <div className="card">
        <h2>Lots récents</h2>
        {!batches.length && <p className="muted">Aucun lot enregistré.</p>}
        {batches.length > 0 && (
          <table>
            <thead><tr><th>Lot</th><th>Date</th><th>Quantités</th><th>Responsable</th><th>Réception</th></tr></thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id}>
                  <td>{b.lot_number}</td>
                  <td>{b.day}</td>
                  <td>{Object.entries(b.quantities).map(([k, v]) => `${k}: ${v}`).join(', ')}</td>
                  <td>{b.operator}</td>
                  <td>{b.reception_ref || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
